<template>
  <div class="min-h-screen  flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-16 w-auto" :src="require('@/assets/images/company_logo.png')" alt="회사 로고" />
      <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
        매장관리웹
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600">
        매장 매니저들을 위한 웹입니다
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-200">
        <form class="space-y-6" @submit.prevent="handleSubmit">
          <div>
            <label for="branchCode" class="block text-sm font-medium text-gray-700">
              매장코드
            </label>
            <div class="mt-1 relative rounded-md shadow-sm">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon class="h-5 w-5 text-gray-400" />
              </div>
              <input id="branchCode" name="branchCode" type="text" pattern="[0-9]{6}" maxlength="6" required
                v-model="branchCode" @input="onBranchCodeInput" :class="{ 'border-red-300': errors.branchCode }"
                class="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="123456" />
            </div>
            <p v-if="errors.branchCode" class="mt-2 text-sm text-red-600">{{ errors.branchCode }}</p>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              비밀번호
            </label>
            <div class="mt-1 relative rounded-md shadow-sm">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockIcon class="h-5 w-5 text-gray-400" />
              </div>
              <input id="password" name="password" :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password" required v-model="password"
                :class="{ 'border-red-300': errors.password }"
                class="appearance-none block w-full pl-10 pr-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="••••••••" />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button type="button" @click="togglePasswordVisibility" class="focus:outline-none">
                  <EyeIcon v-if="showPassword" class="h-5 w-5 text-gray-400" />
                  <EyeOffIcon v-else class="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
            <p v-if="errors.password" class="mt-2 text-sm text-red-600">{{ errors.password }}</p>
          </div>

          <div class="flex items-center justify-between">
            <!-- <div class="flex items-center">
              <input id="remember-me" name="remember-me" type="checkbox" v-model="rememberMe"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
              <label for="remember-me" class="ml-2 block text-sm text-gray-900">
                로그인 상태 유지
              </label>
            </div> -->
            <div></div>
            <div class="text-sm">
              <router-link to="/forgot-password" class="font-medium text-blue-600 hover:text-blue-500">
                비밀번호를 잊으셨나요?
              </router-link>
            </div>
          </div>

          <div>
            <button type="submit" :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <LoaderIcon v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
              <span v-if="isLoading">인증 중...</span>
              <span v-else>매니저 로그인</span>
            </button>
          </div>
        </form>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">
                주의사항
              </span>
            </div>
          </div>
          <p class="mt-3 text-xs text-center text-gray-500">
            내부 임직원분들은 테스트매장 계정으로 로그인해주세요
            <br />*문의 02-527-5883
          </p>
          <div class="mt-4">
            <a href="https://work.agabang.com" target="_blank"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
              내부 임직원용 워크플랫폼
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { UserIcon, LockIcon, EyeIcon, EyeOffIcon, LoaderIcon } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { authAPI, saveToken } from '@/api/apiClient'
import { useAuthStore } from '@/stores/auth' // Pinia 스토어 사용 예시

const router = useRouter()
const authStore = useAuthStore()

const branchCode = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(true)
const isLoading = ref(false)
const errors = reactive({
  branchCode: '',
  password: ''
})

const validateForm = () => {
  let isValid = true
  errors.branchCode = ''
  errors.password = ''

  if (!branchCode.value) {
    errors.branchCode = '매장코드를 입력해주세요.'
    isValid = false
  } else if (!/^\d{6}$/.test(branchCode.value)) {
    errors.branchCode = '매장코드는 6자리 숫자여야 합니다.'
    isValid = false
  }

  if (!password.value) {
    errors.password = '비밀번호를 입력해주세요.'
    isValid = false
  }

  return isValid
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isLoading.value = true
  errors.branchCode = ''
  errors.password = ''

  try {
    const response = await authAPI.login(
      branchCode.value,
      password.value,
      rememberMe.value
    )

    const { login_branch, access_token } = response.data.data

    // 토큰 저장
    saveToken(access_token, rememberMe.value)
    
    // 사용자 정보 저장
    authStore.setUser({
      id: login_branch.id,
      branchCode: login_branch.branch_code,
      name: login_branch.name,
      branchName: login_branch.branch_name,
      email: login_branch.email,
      hp: login_branch.hp,
      requirePasswordChange: login_branch.require_password_change,
      brandType: login_branch.brand_type,
      distributionType: login_branch.distribution_type,
    })
    // 리다이렉트 처리
    const redirectPath = router.currentRoute.value.query.redirect
    if (redirectPath) {
      router.push(redirectPath)
    } else {
      router.push('/')
    }
    // 무조건 select 페이지로 이동
    // router.push('/select')
    
  } catch (error) {
    console.error('로그인 실패:', error)
    
    // 서버에서 전달된 에러 메시지 처리
    if (error.response?.data) {
      const { code, message } = error.response.data
      if (code === 200) {  // IncorrectIDOrPassword 에러 코드
        errors.password = message
      } else {
        errors.branchCode = message || '서버 오류가 발생했습니다.'
      }
    } else {
      errors.branchCode = '서버와의 통신 중 오류가 발생했습니다.'
    }
  } finally {
    isLoading.value = false
  }
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const onBranchCodeInput = (event) => {
  // 숫자가 아닌 문자 제거
  event.target.value = event.target.value.replace(/[^0-9]/g, '');
  branchCode.value = event.target.value;
}
</script>